<template>
  <v-card min-height="90vh" id="tract-form">
    <v-card-title class="primary white--text">
      <TractStatusIcon
      v-if="tract"
      :status="tract.status"
      isHeader
      class="lh-0"
      />
      <span class="mr-2">{{titleText}}</span>
      <v-spacer></v-spacer>
      <v-btn
      v-if="isEditing"
      id="edit-tract-status"
      text
      dense
      color="tertiary"
      @click="editTractStatusDialog = true">
        <span>{{$t('editStatus')}}</span>
        <v-icon small right color="tertiary">mdi-pencil</v-icon>
      </v-btn>
      <BaseDialogActions elementId="tract-form-dialog-actions" hideRefresh/>
    </v-card-title>
    <v-card-text class="py-0">
      <v-container fluid>
        <v-row v-if="!tract" class="mt-6" justify="center" align="center">
          <v-progress-circular indeterminate color="primary"/>
        </v-row>

        <v-row v-else>
          <v-col :cols="12" :lg="isEditing && !hideNotes && !editingPolygon ? 10 : 12">
            <v-tabs v-model="currentTab" color="primary" id="tract-form-tabs" v-if="!editingPolygon">
              <v-tab id="general">
                <span>
                  {{$t('general')}}
                  <Icon
                  v-if="missingGeneralFields.length > 0"
                  icon="mdi-alert-circle"
                  :iconColor="isEditing ? 'warning' : 'error'"
                  margin="ml-1"
                  dataTestId="general-tab-missing-requirements"
                  :tooltipText="$t('tractMissingCreationRequirements', { fields: requiredFieldNames(missingGeneralFields) })"/>
                </span>
              </v-tab>
              <v-tab id="geography">
                <span>
                  {{$t('geography')}}
                  <Icon
                  v-if="missingGeographyFields.length > 0"
                  icon="mdi-alert-circle"
                  :iconColor="isEditing ? 'warning' : 'error'"
                  margin="ml-1"
                  dataTestId="geography-tab-missing-requirements"
                  :tooltipText="$t('tractMissingCreationRequirements', { fields: requiredFieldNames(missingGeographyFields) })"/>
                </span>
              </v-tab>
              <v-tab id="due-diligence" v-if="isEditing && showDueDiligence">{{$t('dueDiligence')}}</v-tab>
              <v-tab id="cruise" v-if="isEditing">{{$t('cruise')}}</v-tab>
              <v-tab id="operational" v-if="isEditing">{{$t('operational')}}</v-tab>
              <v-tab id="contracts" v-if="isEditing">{{$t('contracts')}}</v-tab>
              <v-tab id="files" v-if="isEditing">{{ $t('files') }}</v-tab>
              <v-tab id="bids" v-if="isEditing && isDeliveredTract">{{ $t('bids') }}</v-tab>
              <v-tab id="visits" v-if="isEditing">{{ $t('visits') }}</v-tab>
              <v-spacer/>
              <v-btn icon v-if="isEditing && $vuetify.breakpoint.lgAndUp && !editingPolygon" class="ma-0">
                <Icon
                :small="false"
                :icon="hideNotes ? 'mdi-book-outline' : 'mdi-book-off-outline'"
                :tooltipText="notesVisibilityTooltip"
                @icon-clicked="hideNotes = !hideNotes"
                />
              </v-btn>
            </v-tabs>

            <v-window v-model="currentTab" touchless>
              <v-window-item class="general-container">
                <GeneralInformation
                  ref="generalInfo"
                  :propOriginalTract="originalTract"
                  :propTract="tract"
                  :propLandowners="landowners"
                  :tractTypeFilter="tractTypeFilter"
                  :autofocusName="!!propSeedTract"
                  @apply-to-contracts-toggled="applyToContractsToggled"
                  @tract-changed="generalInformationMutated"
                  @cost-set="tract.cost = $event"
                  @ownership-changed="ownershipMutated"
                  @show-payment-form="showPaymentForm"
                />
              </v-window-item>

              <v-window-item class="geography-container">
                <TractGeography
                :propTract="tract"
                :originalTract="originalTract"
                :isEditing="isEditing"
                :notesHidden="hideNotes"
                @tract-changed="onGeographyMutated"
                @angel-geojson-mutated="onAngelGeojsonMutated"
                @cut-layer-changed="cutLayerChanged = true"
                @mode-changed="geographyModeChanged"/>
              </v-window-item>

              <v-window-item v-if="isEditing && showDueDiligence" class="due-diligence-container">
                <DueDiligence
                :tract="tract"
                :refreshDueDiligence="refreshDueDiligence"
                @due-diligence-updated="getTract(tract.tractId)"
                @due-diligence-refreshed="refreshDueDiligence = false"/>
              </v-window-item>

              <v-window-item v-if="isEditing" class="cruise-container">
                <CruiseData
                  :flat="true"
                  :tract="tract"
                  :requirementHint="tractContentsRequirementHint"
                  :refreshCruiseData="refreshCruiseData"
                  @tract-contents-updated="tractContentAdded"
                  @cruise-data-refreshed="refreshCruiseData = false"
                  @cruise-data-changed="refreshTractCost"
                />
              </v-window-item>

              <v-window-item v-if="isEditing" class="operational-container">
                <Operational
                :tract="tract"
                :savedTract="originalTract"
                :landowners="landowners"
                :refreshTractDestinations="refreshTractDestinations"
                @refresh-tract-contracts="refreshTractContracts = true"
                @tract-destinations-refreshed="refreshTractDestinations = false"
                @open-cruise-tab="openCruiseTab"
                />
              </v-window-item>

              <v-window-item v-if="isEditing" class="contracts-container">
                <TractContracts
                :tractId="tract.tractId"
                :refreshTractContracts="refreshTractContracts"
                @refresh-tract-destinations="refreshTractDestinations = true"
                @tract-contracts-refreshed="refreshTractContracts = false"
                />
              </v-window-item>

              <v-window-item v-if="isEditing" class="files-container">
                <TractFiles :tract="tract" :isEditing="isEditing"/>
              </v-window-item>

              <v-window-item v-if="isEditing && isDeliveredTract" class="bids-container">
                <TractBids :tractId="tract.tractId" :tract="tract" @outcome-saved="onBidOutcomeSaved"/>
              </v-window-item>

              <v-window-item v-if="isEditing" class="visits-container">
                <TractVisits :tract="tract" isEditing/>
              </v-window-item>
            </v-window >

          </v-col>
          <v-col class="mt-6 notes-container" cols="12" lg="2" v-if="isEditing && ((!hideNotes || $vuetify.breakpoint.mdAndDown) && !editingPolygon)">
            <TractNotes
            :tractId="tract.tractId"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions v-if="!editingPolygon">
      <v-container fluid class="px-0">
        <v-row justify="end" align="center" dense>
          <v-col cols="auto">
            <v-btn
            v-if="isEditing"
            class="primary"
            @click="trySaveTract(true)"
            id="tract-form-save">
              {{ $t('save') }}
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
            class="primary"
            @click="trySaveTract()"
            :id="saveBtnId"
            >
              {{saveBtnText}}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
    <v-dialog v-model="editTractStatusDialog" width="400">
      <EditTractStatus
      v-if="tract"
      :tract="tract"
      :propStatus="tract.status"
      @close="closeEditStatusForm"/>
    </v-dialog>
    <v-dialog
      v-if="confirmSubmitDialog"
      v-model="confirmSubmitDialog"
      width="400px"
    >
      <ConfirmDialog
        :title="$t('confirmSaveChanges')"
        :body="$t('confirmTractUpdateMessage')"
        color="primary"
        :confirmText="$t('confirm')"
        @confirm="saveTract"
        @cancel="confirmSubmitDialog = false"
      />
    </v-dialog>

    <v-dialog
    v-model="paymentForm"
    max-width="1200px"
    v-if="paymentForm">
      <TractPaymentForm
        v-if="paymentForm"
        :tractId="tract.tractId"
        :performanceBonds="[]"
        restrictToCost
        @close="paymentForm = false"
        @submitted="costPaymentSubmitted"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { getDefaultTract } from '@/components/tract/tract-form/FormHelpers.js'
import { localToUTC } from '@/utils/DateFormatter.js'
import { TractStatus } from '@/utils/Enumerations.js'
import { endangeredSpeciesEnabled } from '@/utils/dueDiligence/mode.js'
import { seedMitigationItems } from '@/utils/dueDiligence/seedMitigationItems.js'
import * as rules from '@/utils/rules.js'
import { formatPercentage } from '@/utils/NumericMutations'
import { TractTypeCategory, TractTypeSpecialization, TractRequirementType, TractRequiredField } from '@/utils/Enumerations'
import { requiredFieldsForTractType, missingRequiredFields, clearedRequiredFields, requiredFieldNames, requiredFieldMessages, requirementAnnotation } from '@/utils/TractRequirements.js'
import { angelGeojsonClient, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { migrateLegacyShapefile } from '@/utils/map/ShapefileMigrationModule' // TODO MK: Remove when no longer necessary
import { cloneDeep } from 'lodash'
import { requiredScalingRegionStates } from '@/utils/constants'

const Tab = {
  General: 0,
  Geography: 1,
  Cruise: 2,
  Operational: 3,
  Contracts: 4,
  Files: 5,
  Bids: 6
}

const geographyFields = new Set([
  TractRequiredField.MapAndParcelNumber,
  TractRequiredField.Acres,
  TractRequiredField.LoggingAccessibilityTag,
  TractRequiredField.ScalingRegion,
  TractRequiredField.PermitNumbers
].map(f => f.value))

export default {
  name: 'TractForm',

  props: {
    tractId: {
      type: Number,
      default: null
    },
    propSeedTract: {
      type: Object,
      default: null
    },
    tractTypeFilter: {
      type: Function,
      default: null
    },
    initialTab: {
      type: String,
      default: null
    }
  },

  components: {
    GeneralInformation: () => import('@/components/tract/tract-form/GeneralInformation.vue'),
    CruiseData: () => import('@/components/tract/cruise-data/CruiseData.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    TractGeography: () => import('@/components/tract/tract-form/Geography.vue'),
    DueDiligence: () => import('@/components/tract/tract-form/DueDiligence.vue'),
    Operational: () => import('@/components/tract/tract-form/Operational.vue'),
    TractStatusIcon: () => import('@/components/tract/TractStatusIcon.vue'),
    TractNotes: () => import('@/components/tract/tract-detail/TractNotes.vue'),
    EditTractStatus: () => import('@/components/tract/tract-form/EditTractStatus.vue'),
    TractContracts: () => import('@/components/tract/tract-detail/TractContracts.vue'),
    TractBids: () => import('@/components/tract/tract-detail/TractBids.vue'),
    TractFiles: () => import('@/components/tract/tract-form/TractFiles.vue'),
    TractVisits: () => import('@/components/tract/tract-visits/TractVisits.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TractPaymentForm: () => import('@/components/tract/tract-payables/TractPaymentForm.vue')
  },

  data: () => ({
    tract: null,
    originalTract: {},
    currentTab: Tab.General,
    applyDatesToContracts: false,
    editTractStatusDialog: false,
    confirmSubmitDialog: false,
    refreshTractContracts: false,
    refreshTractDestinations: false,
    refreshCruiseData: false,
    hideNotes: false,
    editingPolygon: false,
    landowners: [],
    originalLandowners: [],
    parcels: null,
    spotOutsideParcel: false,
    angelGeojson: null,
    cutLayerChanged: false,
    refreshDueDiligence: false,
    paymentForm: false,
    requiredScalingRegionStates
  }),

  watch: {
    currentTab (newVal, oldVal) {
      this.editingPolygon = false
      const leavingGeneral = newVal !== Tab.General && oldVal === Tab.General
      const openingGeneral = newVal === Tab.General && oldVal !== Tab.General
      const openingGeography = newVal !== Tab.Geography && oldVal === Tab.Geography
      if (leavingGeneral) this.hideNotes = true
      if (openingGeneral) this.hideNotes = false
      if (openingGeography) this.setAllowEscapeToClose(true)
    }
  },

  computed: {
    ...mapGetters('user-settings', ['mutatedUserSettings']),
    ...mapGetters('certification', ['allCertifications']),
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('tract-type', ['allTractTypes']),

    selectedTractType () {
      return this.allTractTypes.find(tt => tt.tractTypeId === this.tract?.type?.tractTypeId)
    },

    tractContentsRequirementHint () {
      return requirementAnnotation(this.selectedTractType, TractRequiredField.TractContents).hint
    },

    missingCreateFields () {
      const createFields = requiredFieldsForTractType(this.selectedTractType, TractRequirementType.Create)
      return missingRequiredFields(this.tract, createFields, { landowners: this.landowners })
    },

    missingGeneralFields () {
      return this.missingCreateFields.filter(field => !geographyFields.has(field.value))
    },

    missingGeographyFields () {
      return this.missingCreateFields.filter(field => geographyFields.has(field.value))
    },

    isEditing () {
      return (this.tractId !== null && this.tractId !== undefined) ||
        (this.tract?.tractId !== undefined && this.tract?.tractId !== null)
    },

    isDeliveredTract () {
      const delivered = t => t?.type?.category === TractTypeCategory.Delivered.value
      return delivered(this.tract) && delivered(this.originalTract)
    },

    showDueDiligence () {
      return endangeredSpeciesEnabled()
    },

    // Due Diligence renders between Geography and Cruise when visible, so it has no Tab entry of
    // its own and shifts every later tab by one — address those through tabIndex().
    dueDiligenceTabIndex () {
      return Tab.Geography + 1
    },

    hasEnteredLocation () {
      if (this.isEditing) return true

      return this.tract.spot !== null
    },

    saveBtnText () {
      if (this.hasEnteredLocation) {
        return this.isEditing ? this.$t('saveAndClose') : this.$t('save')
      }

      return this.$t('chooseLocation')
    },

    saveBtnId () {
      if (this.hasEnteredLocation) {
        return this.isEditing ? 'tract-form-save-and-close' : 'tract-form-save'
      }

      return 'tract-form-choose-location'
    },

    statusText () {
      if (this.tract === null) return ''
      return TractStatus.fromInt(this.tract.status)
    },

    titleText () { return this.isEditing ? this.$t('editing', { name: this.tract?.name }) : this.$t('createNewTract') },

    notesVisibilityTooltip () {
      return this.$t(this.hideNotes ? 'show' : 'hide').concat(` ${this.$t('tractNotes')}`)
    },

    tractChangesWillUpdateContracts () {
      /* eslint-disable */
      // Disabled in order to compare null and undefined with type conversion
      return this.isEditing &&
        (this.applyDatesToContracts ||
        (this.tract.loggerAccountId != this.originalTract.loggerAccountId) ||
        (this.tract.haulerAccountId != this.originalTract.haulerAccountId) ||
        (this.tract.supplierAccountId != this.originalTract.supplierAccountId) ||
        (this.tract.consultingForesterAccountId != this.originalTract.consultingForesterAccountId) ||
        (this.landownersMutated) ||
        (this.tract.type?.specialization !== this.originalTract.type?.specialization))
      /* eslint-enable */
    },

    landownersMutated () {
      if (this.originalLandowners.length !== this.landowners.length) return true
      return !this.originalLandowners.every(original => this.landowners.some(owner => original.accountId === owner.accountId && original.ownership === owner.ownership))
    },

    tractSpotIsUnsaved () {
      function formatCoord (coord) { return parseFloat(coord.toFixed(6)) }
      const { latitude: oldLat, longitude: oldLng } = this.originalTract.spot
      const { latitude: newLat, longitude: newLng } = this.tract.spot
      return formatCoord(oldLat) !== formatCoord(newLat) || formatCoord(oldLng) !== formatCoord(newLng)
    }
  },

  created () {
    this.getTract(this.tractId)
    this.getLandowners(this.tractId)
    if (this.initialTab === 'dueDiligence' && this.isEditing && this.showDueDiligence) {
      this.currentTab = this.dueDiligenceTabIndex
    }
  },

  beforeDestroy () {
    this.updateUserSettings(this.mutatedUserSettings)
  },

  methods: {
    requiredFieldNames,
    ...mapMutations('dialog', ['setAllowEscapeToClose']),
    ...mapActions('tract', ['getTractWithId', 'createTract', 'updateTract', 'fetchTractLandowners']),
    ...mapActions('user-settings', ['updateUserSettings']),
    async getTract (tractId) {
      if (tractId) {
        const tractFromServer = await this.getTractWithId(tractId)
        tractFromServer.foresterUserId = tractFromServer.foresterUser?.applicationUserId
        tractFromServer.loggerIds = tractFromServer.loggers?.map(l => l.accountId)
        tractFromServer.userIds = tractFromServer.users?.map(u => u.applicationUserId)
        tractFromServer.certificationIds = tractFromServer.tractCertifications?.map(t => t.certificationId)
        tractFromServer.certificationAssignments = tractFromServer.tractCertifications?.map(t => ({
          certificationId: t.certificationId,
          basisAccountCertificationId: t.basisAccountCertificationId ?? null
        }))
        tractFromServer.harvestTypeId = tractFromServer.harvestType?.harvestTypeId
        tractFromServer.loggingMethodId = tractFromServer.loggingMethod?.loggingMethodId
        tractFromServer.timberCategoryId = tractFromServer.timberCategory?.timberCategoryId
        tractFromServer.conversionId = tractFromServer.conversion?.conversionId
        tractFromServer.landownerTypeId = tractFromServer.landownerCount > 0 ? null : tractFromServer.landownerType?.landownerTypeId
        tractFromServer.ticketBookIds = tractFromServer.ticketBooks?.map(b => b.ticketBookId)
        this.tract = cloneDeep(tractFromServer)
        this.originalTract = cloneDeep(tractFromServer)
        await migrateLegacyShapefile(this.tract)
      } else {
        this.tract = { ...getDefaultTract(), ...this.propSeedTract }
      }
    },

    async getLandowners (tractId) {
      if (!tractId) return
      this.landowners = await this.fetchTractLandowners(tractId)
      this.originalLandowners = JSON.parse(JSON.stringify(this.landowners))
    },

    applyToContractsToggled (shouldApplyDates) {
      this.applyDatesToContracts = shouldApplyDates
    },

    async refreshTractCost () {
      const tractFromServer = await this.getTractWithId(this.tract.tractId)
      this.tract.cost = tractFromServer.cost
    },

    async onBidOutcomeSaved () {
      const stamped = await this.getTractWithId(this.tract.tractId)
      const applyStamps = (target) => {
        target.harvestStartDate = stamped.harvestStartDate
        target.harvestEndDate = stamped.harvestEndDate
        target.supplierAccountId = stamped.supplierAccountId
        target.contractAccountId = stamped.contractAccountId
        target.status = stamped.status
        target.modifiedAt = stamped.modifiedAt
      }
      // Winning stamps these server-side, so originalTract has to move too or the next save reads them as user edits and prompts for contract approval.
      applyStamps(this.tract)
      applyStamps(this.originalTract)
      if (this.$refs.generalInfo) this.$refs.generalInfo.applyStampedValues(stamped)
      this.refreshTractDestinations = true
      this.refreshCruiseData = true
    },

    tractContentAdded (newTractContents) {
      this.tract.speciesContentCount = newTractContents.filter(content => content.speciesId != null).length
      if (!this.tract.tractContents) {
        this.tract.tractContents = newTractContents
        return
      }

      this.tract.tractContents = this.tract.tractContents.map(existingContent => {
        const updatedContent = newTractContents.find(content => content.tractContentId === existingContent.tractContentId)
        return updatedContent || existingContent
      })

      const newContents = newTractContents.filter(content =>
        !this.tract.tractContents.some(existingContent => existingContent.tractContentId === content.tractContentId)
      )

      this.tract.tractContents.push(...newContents)
    },

    closeEditStatusForm (newStatus) {
      if (newStatus !== undefined) {
        this.getTract(this.tractId)
      }

      this.editTractStatusDialog = false
    },

    trySaveTract (saveSilently = false) {
      if (this.companyInfo.requireApprovalForContractModifications && this.tractChangesWillUpdateContracts) {
        this.confirmSubmitDialog = true
      } else {
        this.saveTract(saveSilently)
      }
    },

    async saveTract (saveSilently = false) {
      if (!this.hasEnteredLocation) {
        this.currentTab = 1
        return
      }

      if (!this.verifyRequest(this.tract) || !this.verifyLandowners(this.landowners)) {
        return
      }

      const request = this.getTractRequestObject(this.tract, this.landowners)

      let tractId = this.tract.tractId
      const wasDepletingTract = this.tract?.type?.depletes
      const wasNewTract = !this.isEditing

      if (this.isEditing) {
        await this.updateTract(request)
        if (!saveSilently) this.$emit('close')
      } else {
        const res = await this.createTract(request)
        this.tract.tractId = res.tractId
        tractId = res.tractId
        this.$emit('tract-created', res.tractId)
      }
      const geojsonUploaded = await this.handleAngelGeojsonUpload()
      if (geojsonUploaded) {
        if (this.cutLayerChanged) {
          await this.maybeSeedMitigationItems(tractId)
          this.refreshDueDiligence = true
        }
        this.angelGeojson = null
        this.cutLayerChanged = false
      }
      await this.getTract(tractId)

      if (wasNewTract && wasDepletingTract) {
        this.showPaymentForm()
      }
    },

    async maybeSeedMitigationItems (tractId) {
      if (!this.angelGeojson) return
      const cut = this.angelGeojson.getLayer
        ? this.angelGeojson.getLayer(AngelGeojsonLayer.Cut)
        : this.angelGeojson.cut
      await seedMitigationItems(tractId, cut)
    },

    verifyLandowners (landowners) {
      const zeroOwnershipAccount = landowners.find(landowner => landowner.ownership === 0)
      if (zeroOwnershipAccount !== undefined) {
        this.setSnackError(this.$t('accountAssignedZeroOwnership', { account: zeroOwnershipAccount.accountName }))
        return false
      }

      if (landowners.some(landowner => landowner.accountId === undefined)) {
        this.setSnackError(this.$t('allLandownersMustBeSet'))
        return false
      }

      const totalOwnership = landowners.reduce((ownership, lo) => ownership + lo.ownership, 0)
      if (Number(totalOwnership.toFixed(2)) !== 100 && landowners.length > 0) {
        this.setSnackError(this.$t('landownershipIsNotAllocated', { actual: formatPercentage(totalOwnership) }))
        return false
      }

      return true
    },

    verifyRequest (tract) {
      if (tract.name.length === 0) {
        this.setSnackError(this.$t('nameFieldIsRequired'))
        return false
      }

      const missingBasis = this.allCertifications.find(c =>
        (c.basisCertificationIds?.length ?? 0) > 0 &&
        tract.certificationIds?.includes(c.certificationId) &&
        !tract.certificationAssignments?.some(a => a.certificationId === c.certificationId && a.basisAccountCertificationId != null))
      if (missingBasis) {
        this.setSnackError(this.$t('certificationRequiresBasis', { certification: missingBasis.name }))
        return false
      }

      if (tract.type.tractTypeId === null) {
        this.setSnackError(this.$t('tractTypeIsRequired'))
        return false
      }

      if (tract.type.category === 0 && tract.businessEntityId === null) {
        this.setSnackError(this.$t('stumpageRequiresEntity'))
        return false
      }

      if (tract.spot === null) {
        this.setSnackError(this.$t('specifyTractLocationInGeography'))
        return false
      }

      if (tract.cost > rules.MAX_TRACT_COST) {
        this.setSnackError(this.$t('invalidTractCost'))
        return false
      }
      if (tract.rpi && (isNaN(parseFloat(tract.rpi)) || rules.default.rules.twoDecimalPlacesOrFewer(tract.rpi) !== true || (tract.rpi > rules.MAX_RPI || tract.rpi < 0))) {
        this.setSnackError(this.$t('invalidEntity', { entity: this.$t('rpi') }))
        return false
      }

      if (tract.dbh && (!/^\d{0,3}$/.test(tract.dbh) || (tract.dbh > rules.MAX_DBH || tract.dbh < 0))) {
        this.setSnackError(this.$t('invalidEntity', { entity: this.$t('dbh') }))
        return false
      }

      if (tract.age && (!/^\d{0,3}$/.test(tract.age) || (tract.age > rules.MAX_AGE || tract.age < 0))) {
        this.setSnackError(this.$t('invalidEntity', { entity: this.$t('age') }))
        return false
      }

      if (tract.teamId && !(tract.userIds?.length > 0)) {
        this.setSnackError(this.$t('selectBothTeamAndUsers'))
        return false
      }

      if (tract.spot.countrySubdivision && this.requiredScalingRegionStates.includes(tract.spot.countrySubdivision) && !tract.scalingRegion) {
        this.setSnackError(this.$t('scalingRegionRequired', { state: tract.spot.countrySubdivision }))
        return false
      }

      const tractType = this.allTractTypes.find(tt => tt.tractTypeId === tract.type?.tractTypeId)
      if (this.isEditing) {
        const cleared = clearedRequiredFields(this.originalTract, tract, requiredFieldsForTractType(tractType), { landowners: this.originalLandowners }, { landowners: this.landowners })
        if (cleared.length > 0) {
          this.setSnackError(this.$t('tractRequiredFieldsCannotBeCleared', { fields: requiredFieldMessages(cleared) }))
          return false
        }
      } else {
        const missing = missingRequiredFields(tract, requiredFieldsForTractType(tractType, TractRequirementType.Create), { landowners: this.landowners })
        if (missing.length > 0) {
          this.setSnackError(this.$t('tractMissingCreationRequirements', { fields: requiredFieldMessages(missing) }))
          return false
        }
      }

      return true
    },

    getTractRequestObject (tract, landowners) {
      const requestObject = {
        name: tract.name,
        code: tract.code,
        paused: tract.paused,
        mapAndParcelNumber: tract.mapAndParcelNumber,
        tractTypeId: tract.type.tractTypeId,
        businessEntityId: tract.businessEntityId,
        foresterUserId: tract.foresterUserId,
        permitNumbers: tract.permitNumbers,
        haulerAccountId: tract.haulerAccountId,
        supplierAccountId: tract.supplierAccountId,
        contractAccountId: tract.contractAccountId,
        contractSettingId: tract.contractSettingId,
        loggerAccountId: tract.loggerAccountId,
        loggerIds: tract.loggerIds,
        loggingAccessibilityTagId: tract.loggingAccessibilityTagId,
        requiresLogger: tract.requiresLogger,
        requiresExt1: tract.requiresExt1,
        requiresExt2: tract.requiresExt2,
        spot: tract.spot,
        cost: tract.cost,
        acres: tract.acres ? parseFloat(tract.acres) : 0,
        certificationIds: tract.certificationIds,
        certificationAssignments: tract.certificationAssignments ?? tract.certificationIds?.map(certificationId => ({ certificationId })),
        purchaseDate: tract.purchaseDate ? localToUTC(tract.purchaseDate) : null,
        harvestStartDate: tract.harvestStartDate ? localToUTC(tract.harvestStartDate) : null,
        harvestEndDate: tract.harvestEndDate ? localToUTC(tract.harvestEndDate) : null,
        availabilityStartDate: tract.availabilityStartDate ? localToUTC(tract.availabilityStartDate) : null,
        availabilityEndDate: tract.availabilityEndDate ? localToUTC(tract.availabilityEndDate) : null,
        status: tract.status,
        pickupRange: tract.pickupRange,
        consultingForesterAccountId: tract.consultingForesterAccountId,
        landowners: landowners.map(lo => ({
          accountId: lo.accountId,
          ownership: lo.ownership
        })),
        rpi: tract.rpi ?? 0.0,
        dbh: tract.dbh ?? 0,
        age: tract.age ?? 0,
        userIds: tract.userIds,
        teamId: tract.teamId ?? tract.team?.teamId,
        harvestTypeId: tract.harvestTypeId,
        loggingMethodId: tract.loggingMethodId,
        timberCategoryId: tract.timberCategoryId,
        conversionId: tract.conversionId,
        landownerTypeId: tract.landownerTypeId,
        ticketBookIds: tract.ticketBookIds,
        scalingRegion: tract.scalingRegion,
        tractGroupId: tract.tractGroupId
      }

      if (this.isEditing) {
        requestObject.tractId = this.tract.tractId
        requestObject.modifiedAt = tract.modifiedAt
        requestObject.shouldApplyDates = this.applyDatesToContracts
      }

      if (tract.type.category === TractTypeCategory.Delivered.value) {
        requestObject.purchaseDate = null
        requestObject.businessEntityId = null
      }

      if (tract.type.category === TractTypeCategory.Stumpage.value && tract.type.specialization === TractTypeSpecialization.Depletion.value) {
        // Wipe out cost if it's a stumpage tract that depletes. The cost is derived from tract payments.
        if (!this.originalTract?.type?.specialization === TractTypeSpecialization.Depletion.value) {
          requestObject.cost = 0
        }
      }

      if (tract.tractGroupId != null) {
        requestObject.landowners = null
      }

      return requestObject
    },

    generalInformationMutated (generalInfo) {
      this.tract.name = generalInfo.name
      this.tract.code = generalInfo.code
      this.tract.foresterUserId = generalInfo.foresterUserId
      this.tract.businessEntityId = generalInfo.businessEntity?.businessEntityId
      this.tract.type = generalInfo.type
      this.tract.loggerAccountId = generalInfo.loggerAccountId
      this.tract.haulerAccountId = generalInfo.haulerAccountId
      this.tract.supplierAccountId = generalInfo.supplierAccountId
      this.tract.contractAccountId = generalInfo.contractAccountId
      this.tract.contractSettingId = generalInfo.contractSettingId
      this.tract.loggerIds = generalInfo.loggerAccountIds
      this.tract.certificationIds = generalInfo.certificationIds
      this.tract.certificationAssignments = generalInfo.certificationAssignments
      this.tract.harvestStartDate = generalInfo.harvestStartDate
      this.tract.harvestEndDate = generalInfo.harvestEndDate
      this.tract.availabilityStartDate = generalInfo.availabilityStartDate
      this.tract.availabilityEndDate = generalInfo.availabilityEndDate
      this.tract.purchaseDate = generalInfo.purchaseDate
      this.tract.requiresExt1 = generalInfo.requiresExt1
      this.tract.requiresExt2 = generalInfo.requiresExt2
      this.tract.requiresLogger = generalInfo.requiresLogger
      this.tract.cost = generalInfo.cost
      this.tract.consultingForesterAccountId = generalInfo.consultingForesterAccountId
      this.tract.rpi = generalInfo.rpi
      this.tract.dbh = generalInfo.dbh
      this.tract.age = generalInfo.age
      this.tract.userIds = generalInfo.userIds
      this.tract.teamId = generalInfo.teamId
      this.tract.harvestTypeId = generalInfo.harvestTypeId
      this.tract.loggingMethodId = generalInfo.loggingMethodId
      this.tract.timberCategoryId = generalInfo.timberCategoryId
      this.tract.conversionId = generalInfo.conversionId
      this.tract.landownerTypeId = generalInfo.landownerTypeId
      this.tract.ticketBookIds = generalInfo.ticketBookIds
      this.tract.tractGroupId = generalInfo.tractGroupId
    },

    ownershipMutated (landowners) {
      this.landowners = landowners
    },

    onGeographyMutated (geographyInfo) {
      this.tract.spot = geographyInfo.spot
      this.tract.mapAndParcelNumber = geographyInfo.mapAndParcelNumber
      this.tract.acres = geographyInfo.acres
      this.tract.pickupRange = geographyInfo.pickupRange
      this.tract.loggingAccessibilityTagId = geographyInfo.loggingAccessibilityTagId
      this.tract.scalingRegion = geographyInfo.scalingRegion
      this.tract.permitNumbers = geographyInfo.permitNumbers
    },

    tabIndex (tab) {
      return this.showDueDiligence && tab > Tab.Geography ? tab + 1 : tab
    },

    openCruiseTab () {
      this.currentTab = this.tabIndex(Tab.Cruise)
    },

    async handleAngelGeojsonUpload () {
      if (!this.angelGeojson) return true
      return await angelGeojsonClient.uploadGeojson(this.tract.tractId, this.angelGeojson)
    },

    geographyModeChanged (mode) {
      if (mode.label === 'polygonEditor') {
        this.editingPolygon = true
      } else {
        this.editingPolygon = false
      }
    },

    onAngelGeojsonMutated (angelGeojson) {
      this.angelGeojson = angelGeojson
    },

    showPaymentForm () {
      this.paymentForm = true
    },

    costPaymentSubmitted (amount) {
      this.paymentForm = false
      this.tract.cost = amount
      this.originalTract.cost = amount
      if (this.$refs.generalInfo) {
        this.$refs.generalInfo.refreshPayables()
      }
    }
  }
}
</script>
